import { useEffect, useState } from 'react';
import {
    Divider,
    useAppDispatch,
    useAppSelector,
    useTheme,
    Stack,
    Skeleton,
} from '../../../base';
import { reportsActions, reportsSelectors } from '../../../actions';
import { ReportPeriod, SourceOfHireViewType } from '../../../common';
import {
    IReportsFilterValue,
    IFetchSourceOfHireParams,
} from '../../../common/interfaces/reports.interfaces';

import ReportsFilter from '../ReportsFilter';
import SourceOfHireViewToggle from './SourceOfHireViewToggle';
import TableView from './TableView';
import SummaryCards from './SummaryCards';
import PieView from './PieView';
import BarView from './BarView';
import {
    StyledSourceOfHireWrapper,
    StyledFilterRow,
    StyledFilterSlot,
    StyledViewToggleSlot,
} from './SourceOfHire.styles';

const buildSourceOfHireParams = (
    filterValue: IReportsFilterValue
): IFetchSourceOfHireParams => {
    const params: IFetchSourceOfHireParams = {};

    if (filterValue.jobIds && filterValue.jobIds.length > 0) {
        params.job_ids = filterValue.jobIds.join(',');
    } else if (filterValue.jobId) {
        params.job_ids = String(filterValue.jobId);
    }

    if (
        filterValue.timeFrame === ReportPeriod.CUSTOM &&
        filterValue.customDateRange
    ) {
        params.time_frame = ReportPeriod.CUSTOM;
        params.custom_from = filterValue.customDateRange.fromDate;
        params.custom_to = filterValue.customDateRange.toDate;
    } else if (filterValue.timeFrame) {
        params.time_frame = filterValue.timeFrame;
    }

    return params;
};

const SourceOfHire = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const stats = useAppSelector(reportsSelectors.getSourceOfHireStats);
    const isLoadingStats = useAppSelector(
        reportsSelectors.getIsLoadingSourceOfHireStats
    );
    const chartData = useAppSelector(reportsSelectors.getSourceOfHireChart);
    const isLoadingChartData = useAppSelector(
        reportsSelectors.getIsLoadingSourceOfHireChart
    );

    const [viewMode, setViewMode] = useState<SourceOfHireViewType>(
        SourceOfHireViewType.TABLE
    );
    const [filterParams, setFilterParams] = useState<IFetchSourceOfHireParams>({
        time_frame: ReportPeriod.LIFETIME,
    });

    useEffect(() => {
        dispatch(
            reportsActions.setSourceOfHireFilter({ filter: filterParams })
        );
    }, [dispatch]);

    useEffect(() => {
        if (viewMode === SourceOfHireViewType.TABLE) {
            dispatch(reportsActions.fetchSourceOfHireStats(filterParams));
        } else {
            dispatch(reportsActions.fetchSourceOfHireChart(filterParams));
        }
    }, [dispatch, viewMode, filterParams]);

    const handleFilterChange = (filterValue: IReportsFilterValue) => {
        const params = buildSourceOfHireParams(filterValue);
        dispatch(reportsActions.setSourceOfHireFilter({ filter: params }));
        setFilterParams(params);
    };

    const renderChartSkeleton = () => (
        <Stack sx={{ padding: theme.spacing(2, 3) }} spacing={2}>
            <Skeleton
                variant="rectangular"
                height={110}
                sx={{ borderRadius: 2 }}
            />
        </Stack>
    );

    const renderView = () => {
        if (viewMode === SourceOfHireViewType.TABLE) {
            return (
                <TableView
                    stats={stats}
                    isLoadingStats={isLoadingStats}
                    reportFilter={filterParams}
                />
            );
        }

        if (isLoadingChartData || !chartData) {
            return renderChartSkeleton();
        }

        const timeFrame = filterParams.time_frame ?? ReportPeriod.LIFETIME;

        return (
            <>
                <SummaryCards
                    topStatistics={chartData.topStatistics}
                    timeFrame={timeFrame}
                />
                {viewMode === SourceOfHireViewType.PIE ? (
                    <PieView
                        chartData={chartData}
                        timeFrame={timeFrame}
                        startDate={filterParams.custom_from ?? null}
                        endDate={filterParams.custom_to ?? null}
                    />
                ) : (
                    <BarView chartData={chartData} />
                )}
            </>
        );
    };

    return (
        <StyledSourceOfHireWrapper>
            <StyledFilterRow>
                <StyledFilterSlot>
                    <ReportsFilter
                        onChange={handleFilterChange}
                        defaultTimeFrame={ReportPeriod.LIFETIME}
                        maxJobs={5}
                        showRecruiterFilter={false}
                    />
                </StyledFilterSlot>
                <StyledViewToggleSlot>
                    <SourceOfHireViewToggle
                        activeView={viewMode}
                        onChange={setViewMode}
                    />
                </StyledViewToggleSlot>
            </StyledFilterRow>
            <Divider sx={{ borderColor: theme.palette.grey[300] }} />
            {renderView()}
        </StyledSourceOfHireWrapper>
    );
};

export default SourceOfHire;
